import { LocalizationService } from '@abp/ng.core';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Select } from '@ngxs/store';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { DateDisplayType } from 'src/ca-shared/conversation-date-filter/models/date-display-type.enum';
import { ConversationListResponseDto } from 'src/core/models/conversation/conversation-list-response.dto';
import { ConversationDto } from 'src/core/models/conversation/conversation.dto';
import { MatTableColumnDefinitionModel } from 'src/core/models/mat-table/mat-table-column-definition.model';
import { CADatePipe } from 'src/core/pipes/ca-date.pipe';
import { ConversationModuleState } from 'src/core/states/conversation/conversation-module.state';
import { ConversationService } from 'src/core/services/conversation/conversation.service';
import { CATimePipe } from 'src/core/pipes/ca-time.pipe';
import { CompactPlayerComponent } from 'src/ca-shared/player/compact-player/compact-player.component';
import { ConversationListType } from 'src/core/models/generic-lookup-type/conversation/conversation-list-type.glt';

@Component({
  selector: 'ca-standard-meeting-list',
  templateUrl: './standard-meeting-list.component.html',
  styleUrls: ['./standard-meeting-list.component.scss'],
  standalone: false,
})
export class StandardMeetingListComponent implements OnInit {
  @Select(ConversationModuleState.getData)
  data$: Observable<ConversationListResponseDto>;

  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<ConversationDto>();
  data: ConversationDto[] = [];
  gridId: string;

  @Input()
  compactPlayer: CompactPlayerComponent;

  @Output()
  conversationIdChange: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  dataLoaded = new EventEmitter();

  gridColumns: MatTableColumnDefinitionModel[] = [
    {
      columnName: 'icons',
      header: this.localizationService.instant('::Icons'),
      binding: 'id',
      valueParser: undefined,
      pipes: undefined,
      width: '185px',
      tooltip: 'Conversation Type',
      locked: true,
      hideHeader: true,
      visible: true,
    },
    {
      columnName: 'conversationId',
      header: 'ID',
      binding: 'id',
      valueParser: undefined,
      pipes: undefined,
      width: '100px',
      minWidth: '100px',
      tooltip: this.localizationService.instant('Conversation::ConversationId'),
      sticky: true,
      headerClass: 'header-align-right',
      cellClass: 'cell-align-right',
      locked: true,
      hideHeader: false,
      visible: true,
    },
    {
      columnName: 'userName',
      header: this.localizationService.instant('::Agent'),
      binding: 'userName',
      valueParser: undefined,
      pipes: undefined,
      width: '150px',
      minWidth: '150px',
      tooltip: undefined,
      locked: false,
      hideHeader: false,
      visible: true,
    },
    {
      columnName: 'departmentName',
      header: this.localizationService.instant('::Department'),
      binding: 'departmentName',
      valueParser: undefined,
      pipes: undefined,
      width: '150px',
      minWidth: '150px',
      tooltip: undefined,
      locked: false,
      hideHeader: false,
      visible: true,
    },
    {
      columnName: 'conversationSourceName',
      header: this.localizationService.instant('::Source'),
      binding: 'conversationSourceName',
      valueParser: undefined,
      pipes: undefined,
      width: '100px',
      tooltip: undefined,
      locked: false,
      hideHeader: false,
      visible: false,
    },
    {
      columnName: 'mainCategoryName',
      header: this.localizationService.instant('::MainCategoryName'),
      binding: 'mainCategoryName',
      valueParser: undefined,
      pipes: undefined,
      width: '100px',
      tooltip: undefined,
      visible: false,
      locked: false,
    },
    {
      columnName: 'startTime',
      header: this.localizationService.instant('::Time'),
      binding: 'startTime',
      valueParser: val => {
        return new Date(val);
      },
      pipes: [
        {
          pipe: CADatePipe,
          args: [DateDisplayType.DateTimeWithoutSeconds, true],
        },
      ],
      width: '150px',
      minWidth: '150px',
      tooltip: undefined,
      sortable: true,
      sorterFieldName: 'StartTime',
      headerClass: 'header-align-right',
      cellClass: 'cell-align-right',
      locked: false,
      hideHeader: false,
      visible: true,
    },
    {
      columnName: 'duration',
      header: this.localizationService.instant('::Duration'),
      binding: 'duration',
      valueParser: undefined,
      pipes: [
        {
          pipe: CATimePipe,
          args: [true],
        },
      ],
      width: '150px',
      minWidth: '150px',
      tooltip: undefined,
      sortable: true,
      sorterFieldName: 'Duration',
      headerClass: 'header-align-right',
      cellClass: 'cell-align-right',
      locked: false,
      hideHeader: false,
      visible: true,
    },
    {
      columnName: 'uniqueCustomerKey',
      header: this.localizationService.instant('Conversation::UniqueCustomerKey'),
      binding: 'uniqueCustomerKey',
      valueParser: undefined,
      pipes: undefined,
      width: '150px',
      tooltip: undefined,
      visible: false,
      locked: false,
    },
    {
      columnName: 'externalId',
      header: this.localizationService.instant('::ExternalId'),
      binding: 'externalId',
      valueParser: undefined,
      pipes: undefined,
      width: '150px',
      tooltip: undefined,
      visible: false,
      locked: false,
    },
    {
      columnName: 'agentUserName',
      header: this.localizationService.instant('::AgentUserName'),
      binding: 'agentUserName',
      valueParser: undefined,
      pipes: undefined,
      width: '150px',
      tooltip: undefined,
      locked: false,
      hideHeader: false,
      visible: false,
    },
    {
      columnName: 'language',
      header: this.localizationService.instant('::Language'),
      binding: 'languageCodeName',
      valueParser: language => {
        if (language) {
          return this.localizationService.instant('Settings::Call.Language.' + language);
        }
      },
      pipes: undefined,
      width: '150px',
      tooltip: undefined,
      sortable: false,
      headerClass: 'header-align-right',
      cellClass: 'cell-align-right',
      locked: false,
      hideHeader: false,
      visible: true,
    },
  ];

  constructor(
    private localizationService: LocalizationService,
    private conversationService: ConversationService
  ) {
    this.gridId = `conversation-list-${ConversationListType.meeting}`;

    this.conversationService.checkAndAddAutomaticEvaluationScoreColumn(this.gridColumns);
    for (const gridColumn of this.gridColumns) {
      this.displayedColumns.push(gridColumn.columnName);
    }

    this.data$.subscribe(data => {
      this.data = data === null ? [] : data.items;

      this.dataSource.connect().next(this.data);
      this.dataLoaded.emit();
    });
  }

  ngOnInit() {}

  onConversationIdChanged(conversationId) {
    this.conversationIdChange.emit(conversationId);
  }
}
